import { useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Loader,
  Alert,
  Box,
  Badge,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import { createPfileIds } from '@lib2/model/pfile/PfileUtil';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PfileImageUploader } from '@/shared/components/PfileImageUploader';
import { CONTENT_IMAGE_SPECS } from '@/lib/constants/pfileImage';
import { APP_CONTENT_QUERY_KEYS } from '@/lib/constants/appContent';
import { useAppContentListlist } from '../useAppContentListlist';
import { useAppContentListlists } from '../useAppContentListlists';
import { useAppContentPositions } from '../useAppContentPositions';
import { useUpdateListlist } from '../useAppContentMutations';
import { AppContentUtils, type CategoryChildView, type ListlistFormData } from '../AppContentUtils';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { useStreamLiveStatus } from '@/features/streams/useStreamLiveStatus';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { listlistFormSchema } from '../schemas/ListlistForm.schema';
import { ListlistSettingsSection } from './ListlistSettingsSection';
import { OrderedChildrenEditor } from './OrderedChildrenEditor';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

export function CategoryDetailPage() {
  const { id: displayId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { selectedCId } = useAuth();

  // Parse base-converted ID
  let llId = 0;
  let idError = false;
  try {
    llId = AppContentUtils.getNumericId(displayId!);
    if (!llId || llId <= 0) idError = true;
  } catch {
    idError = true;
  }

  const { data: listlist, isLoading, isError, error } = useAppContentListlist(llId);
  const { series } = useAppContentListlists();
  const { data: positions } = useAppContentPositions();
  const updateMutation = useUpdateListlist(llId);

  const llr = listlist?.llr ?? null;
  const isActive = llr ? llr.mediaStateTypeId === MediaStateTypeId.Active : false;

  // Form for settings
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ListlistFormData>({
    resolver: zodResolver(listlistFormSchema),
    defaultValues: {
      title: '',
      descShort: '',
      mediaStateTypeId: MediaStateTypeId.Active,
    },
  });

  // Sync form when data loads
  useEffect(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  const handleSave = handleSubmit((data: ListlistFormData) => {
    const req = AppContentUtils.buildUpdateReq(data);
    updateMutation.mutate(req, {
      onSuccess: () => {
        // Clean baseline immediately — the refetch-driven effect reset arrives
        // later; without this the save bar stays up for the whole round-trip.
        reset(data);
        notifications.show({
          title: 'Saved',
          message: 'Category settings updated.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  // Get the ordered children (series AND directly placed items, design
  // ADR-0014) from positions. categoryNodes ONLY: every category has an
  // authoritative top-level entry there. Categories also appear nested in
  // rootNodes trees; getOrderedChildren is first-match-wins, so always search
  // the array that owns this level.
  const orderedChildren = useMemo(
    () => (positions ? AppContentUtils.getOrderedChildren(llId, positions.categoryNodes ?? []) : null),
    [positions, llId],
  );

  const orderedRefs = useMemo(() => orderedChildren?.children ?? [], [orderedChildren]);
  const placedMediaIds = orderedChildren?.mediaIds ?? [];

  // Item titles, art and kinds come from the account's libraries; only fetched
  // once a category actually places items (a 30k-video account pays nothing
  // otherwise). The on-air poll runs only while a live stream is placed.
  const hasItems = placedMediaIds.length > 0;
  const { data: linkConfig } = useSharedLinkConfig();
  const { videos } = useVideos({ enabled: hasItems, refetchOnMount: false });
  const { streams } = useStreams({ enabled: hasItems, refetchOnMount: false });
  const streamsById = useMemo(() => new Map(streams.map((st) => [st.media.mediaId, st])), [streams]);
  const hasLiveItems = useMemo(
    () => placedMediaIds.some((id) => streamsById.has(id)),
    [placedMediaIds, streamsById],
  );
  const { liveStatusMap } = useStreamLiveStatus({ enabled: hasLiveItems });

  // Build lookup for series details
  const seriesLookup = useMemo(() => new Map(series.map((s) => [s.llr.llId, s])), [series]);
  const seasonCountByLlId = useMemo(
    () => AppContentUtils.seasonCountsFromSeriesNodes(positions?.seriesNodes),
    [positions],
  );

  // Every child resolved once per data change — the editor rows, the Home-row
  // preview and the remove dialog all read this map.
  const childViews = useMemo(() => {
    const ctx = {
      seriesById: seriesLookup,
      seasonCountByLlId,
      videosById: new Map(videos.map((v) => [v.media.mediaId, v])),
      streamsById,
      linkConfig: linkConfig ?? null,
      liveStatusMap,
    };
    const map = new Map<string, CategoryChildView>();
    for (const view of AppContentUtils.buildCategoryChildViews(orderedRefs, ctx)) {
      map.set(view.key, view);
    }
    return map;
  }, [orderedRefs, seriesLookup, seasonCountByLlId, videos, streamsById, linkConfig, liveStatusMap]);

  // ── Error states ──

  if (idError) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'categories' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Invalid category ID.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading category...</Text>
      </Stack>
    );
  }

  if (isError || !llr) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'categories' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message ?? 'Failed to load category'}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Back link */}
      <Box>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'categories' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
      </Box>

      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{llr.title}</Title>
          <Group gap="xs" mt={4}>
            <Text size="sm" c="dimmed" ff="monospace">
              {AppContentUtils.getDisplayId(llr.llId)}
            </Text>
            <Badge variant="light" color={isActive ? 'green' : 'gray'} size="sm">
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Box>
      </Group>

      {/* Settings Form */}
      <form onSubmit={handleSave}>
        <Text fw={600} size="lg">
          Settings
        </Text>
        <FormSaveBar
          isDirty={isDirty}
          isPending={updateMutation.isPending}
          onDiscard={handleDiscard}
        />

        <Box mt="md">
          <ListlistSettingsSection control={control} />
        </Box>
      </form>

      {/* Poster Image */}
      <Box mt="xl">
        {CONTENT_IMAGE_SPECS.map((spec) => (
          <PfileImageUploader
            key={spec.pfileTypeId}
            pfiles={listlist?.files ?? []}
            spec={spec}
            pfileIds={createPfileIds({
              llId,
              pfileTypeId: spec.pfileTypeId,
            })}
            invalidateQueryKeys={
              selectedCId
                ? [
                    APP_CONTENT_QUERY_KEYS.detail(selectedCId, llId),
                    APP_CONTENT_QUERY_KEYS.listlists(selectedCId),
                  ]
                : []
            }
          />
        ))}
      </Box>

      {/* Ordered children: series and items in one sequence (ADR-0014) */}
      <Box mt="xl">
        <OrderedChildrenEditor
          parentLlId={llId}
          parentType={ListlistTypeId.Category}
          orderedChildren={orderedRefs}
          allAvailableChildren={series}
          childLookup={seriesLookup}
          childDetailRoute="/apps/content/series"
          childViews={childViews}
          seasonCountByLlId={seasonCountByLlId}
          parentTitle={llr.title}
        />
      </Box>
    </Stack>
  );
}
